// ================================================================================
// SharedComponents.jsx — Reusable atomic UI components
// ================================================================================

import React from "react";

// ---------------------------------------------------------------------------
// WardStat
// ---------------------------------------------------------------------------
export const WardStat = ({ icon, label, value }) => (
    <div className="flex items-center space-x-2 bg-slate-800/50 p-2 rounded-lg border border-slate-700/30">
        <div className="text-accent-400">{icon}</div>
        <div className="overflow-hidden">
            <div className="text-xs text-slate-400 truncate">{label}</div>
            <div className="text-sm font-semibold text-white truncate" title={value}>{value ?? 'N/A'}</div>
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// GradeBadge
// ---------------------------------------------------------------------------
export const GradeBadge = ({ grade }) => {
    const config = {
        'A+': { color: 'text-cyan-400', glow: 'shadow-[0_0_30px_-5px_rgba(34,211,238,0.3)]', stroke: 'stroke-cyan-400' },
        'A':  { color: 'text-emerald-400', glow: 'shadow-[0_0_30px_-5px_rgba(52,211,153,0.3)]', stroke: 'stroke-emerald-400' },
        'B':  { color: 'text-yellow-400', glow: 'shadow-[0_0_30px_-5px_rgba(250,204,21,0.3)]', stroke: 'stroke-yellow-400' },
        'C':  { color: 'text-orange-400', glow: 'shadow-[0_0_30px_-5px_rgba(251,146,60,0.3)]', stroke: 'stroke-orange-400' },
        'D':  { color: 'text-red-400', glow: 'shadow-[0_0_30px_-5px_rgba(248,113,113,0.3)]', stroke: 'stroke-red-400' },
        'F':  { color: 'text-red-600', glow: 'shadow-[0_0_30px_-5px_rgba(220,38,38,0.3)]', stroke: 'stroke-red-600' },
        'N/A': { color: 'text-slate-500', glow: '', stroke: 'stroke-slate-600' }
    }[grade] || { color: 'text-slate-500', glow: '', stroke: 'stroke-slate-600' };

    return (
        <div className={`relative flex flex-col items-center justify-center p-4 rounded-xl border border-white/5 bg-slate-900/40 backdrop-blur-xl ${config.glow} overflow-hidden`}>
            <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-white/5 to-transparent rounded-full blur-2xl -mr-10 -mt-10 pointer-events-none"></div>
            <div className="absolute top-3 left-4 text-[10px] font-bold text-slate-500 uppercase tracking-widest">Strategy Grade</div>
            <div className="relative w-20 h-20 mt-4 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36">
                    <path className="text-slate-800" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" fill="none" strokeWidth="2.5" />
                    <path
                        className={`${config.stroke} transition-all duration-1000 ease-out`}
                        strokeDasharray="100, 100"
                        d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                        fill="none"
                        strokeWidth="3"
                        strokeLinecap="round"
                    />
                </svg>
                <div className={`absolute text-3xl font-black ${config.color} tracking-tighter`}>{grade}</div>
            </div>
            <div className="mt-2 text-[10px] text-slate-400 font-medium">Plato Analysis</div>
        </div>
    );
};

// ---------------------------------------------------------------------------
// MiniBar (legacy support)
// ---------------------------------------------------------------------------
export const MiniBar = ({ score, color = "bg-accent-500" }) => (
    <div className="w-16">
        <div className="w-full bg-slate-700/50 rounded-full h-1">
            <div className={`${color} h-1 rounded-full`} style={{ width: `${Math.min(score ?? 0, 100)}%` }}></div>
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// ModernProgressBar
// ---------------------------------------------------------------------------
export const ModernProgressBar = ({ label, score, colorClass = "bg-accent-500" }) => (
    <div className="w-full group">
        <div className="flex justify-between items-end text-xs mb-1.5">
            <span className="text-slate-400 font-medium group-hover:text-white transition-colors">{label}</span>
            <span className="font-mono text-white font-bold">{(score ?? 0).toFixed(0)}%</span>
        </div>
        <div className="w-full bg-slate-700/30 rounded-full h-1.5 overflow-hidden border border-white/5">
            <div
                className={`h-full rounded-full shadow-[0_0_10px_rgba(0,0,0,0.3)] transition-all duration-1000 ease-out ${colorClass}`}
                style={{ width: `${Math.min(score ?? 0, 100)}%` }}
            />
        </div>
    </div>
);

// ---------------------------------------------------------------------------
// ModernMiniGauge
// ---------------------------------------------------------------------------
export const ModernMiniGauge = ({ score, gradientClass }) => (
    <div className="w-full h-1.5 bg-slate-700/30 rounded-full overflow-hidden border border-white/5 mt-1.5">
        <div
            className={`h-full rounded-full shadow-sm transition-all duration-1000 ease-out ${gradientClass}`}
            style={{ width: `${Math.min(score ?? 0, 100)}%` }}
        />
    </div>
);

// ---------------------------------------------------------------------------
// StrategicLeverageGauge
// ---------------------------------------------------------------------------
import { CheckSquare, TrendingUp, Activity, AlertTriangle, Zap, Info } from "lucide-react";

export const StrategicLeverageGauge = ({ leverageData }) => {
    if (!leverageData) return null;

    const { leverage_score, leverage_status, projected_vote_lift, votes_needed_to_win, rationale } = leverageData;
    const percentage = Math.min(leverage_score * 100, 100);

    const getStatusConfig = () => {
        const styles = {
            'WIN_PROJECTED':     { label: 'Win Projected',     color: 'text-emerald-400', bg: 'bg-emerald-500', border: 'border-emerald-500/30', icon: <CheckSquare size={16}/> },
            'COMPETITIVE':       { label: 'Competitive',       color: 'text-cyan-400',    bg: 'bg-cyan-500',    border: 'border-cyan-500/30',    icon: <TrendingUp size={16}/> },
            'CONTRIBUTING':      { label: 'Contributing',      color: 'text-yellow-400',  bg: 'bg-yellow-500',  border: 'border-yellow-500/30',  icon: <Activity size={16}/> },
            'INEFFICIENT':       { label: 'Inefficient',       color: 'text-red-400',     bg: 'bg-red-500',     border: 'border-red-500/30',     icon: <AlertTriangle size={16}/> },
            'HIGH_IMPACT_EVENT': { label: 'High Impact Event', color: 'text-amber-400',   bg: 'bg-amber-500',   border: 'border-amber-500/30',   icon: <Zap size={16}/> },
            'NEUTRAL':           { label: 'Neutral',           color: 'text-slate-400',   bg: 'bg-slate-500',   border: 'border-slate-500/30',   icon: <Info size={16}/> }
        };

        if (leverage_status !== 'INEFFICIENT' && percentage > 0 && percentage < 15) return styles['CONTRIBUTING'];
        if (styles[leverage_status]) return styles[leverage_status];
        if (percentage >= 100) return styles['WIN_PROJECTED'];
        if (percentage >= 50)  return styles['COMPETITIVE'];
        if (percentage >= 15)  return styles['HIGH_IMPACT_EVENT'];
        if (percentage > 0)    return styles['CONTRIBUTING'];
        return styles['NEUTRAL'];
    };

    const statusConfig = getStatusConfig();

    return (
        <div className={`relative overflow-hidden rounded-xl bg-slate-800/60 border ${statusConfig.border} p-5 mb-6`}>
            <div className={`absolute top-0 right-0 w-32 h-32 ${statusConfig.bg} opacity-5 blur-3xl rounded-full -mr-10 -mt-10 pointer-events-none`}></div>

            <div className="flex justify-between items-start mb-4 relative z-10">
                <div>
                    <div className="flex items-center gap-2 mb-1">
                        <div className={`p-1.5 rounded-md bg-slate-900/50 ${statusConfig.color} border border-white/5`}>
                            {statusConfig.icon}
                        </div>
                        <h5 className="text-sm font-bold text-white uppercase tracking-wider">Strategic Leverage</h5>
                    </div>
                    <p className="text-xs text-slate-400">Campaign Lift vs. Win Requirement</p>
                </div>
                <div className="text-right">
                    <div className={`text-2xl font-black ${statusConfig.color} font-mono`}>{percentage.toFixed(1)}%</div>
                    <div className="text-[10px] text-slate-500 uppercase font-bold tracking-wider">Gap Closed</div>
                </div>
            </div>

            <div className="relative w-full h-3 bg-slate-900/80 rounded-full overflow-hidden mb-3 border border-slate-700/50">
                <div className="absolute top-0 bottom-0 w-0.5 bg-white/30 z-20 left-[100%]"></div>
                <div
                    className={`h-full ${statusConfig.bg} transition-all duration-1000 ease-out relative z-10`}
                    style={{ width: `${Math.max(percentage, 2)}%` }}
                >
                    <div className="absolute top-0 bottom-0 right-0 w-full bg-gradient-to-l from-white/20 to-transparent"></div>
                </div>
            </div>

            <div className="flex justify-between items-center text-xs relative z-10 border-t border-slate-700/50 pt-3 mt-3">
                <div className="flex flex-col">
                    <span className="text-slate-500 uppercase text-[10px] font-bold">Projected Lift</span>
                    <span className={`font-mono font-bold ${statusConfig.color}`}>+{projected_vote_lift.toLocaleString()} Votes</span>
                </div>
                <div className="flex flex-col items-center">
                    <span className="text-slate-500 uppercase text-[10px] font-bold">Status</span>
                    <span className={`font-bold ${statusConfig.color}`}>{statusConfig.label}</span>
                </div>
                <div className="flex flex-col items-end">
                    <span className="text-slate-500 uppercase text-[10px] font-bold">Required Swing</span>
                    <span className="font-mono font-bold text-slate-300">{votes_needed_to_win.toLocaleString()} Votes</span>
                </div>
            </div>

            <div className="mt-3 bg-slate-900/40 rounded p-2 border border-white/5 text-xs text-slate-300 italic leading-relaxed">
                "{rationale}"
            </div>
        </div>
    );
};
